import React, {
  Fragment,
  useState,
  useEffect,
  useMemo,
  useRef,
  useCallback
} from 'react'
import {
  Grid,
  IconButton,
  Button,
  Box,
  Typography,
  Collapse,
  Paper
} from '@mui/material'
import { KeyboardArrowDown, KeyboardArrowUp, Sync, PublishedWithChanges } from '@mui/icons-material'
import { AsnAdjustDetail } from '../../../interfaces/Inbound/InboundAdjust'
import { AsnAdjustWorkOrderLpn } from '../../../interfaces/AsnAdjust/AsnAdjust'
import { CellText } from '../../../components/DataGrid/CellText'
import { StatusIcon } from '../../../components/_WMS/StatusIcon/StatusIcon'
import { useAdjustments } from '../hooks/useAdjustments'
import { GridElementModalDetailNew } from './GridElementModal/GridElementModalDetailNew'
import { dataGridSettings } from './GridElementModal/DataGridSettings'
import { Filter } from '../../../components/_WMS/FilterComponent'
import { Loading } from '../../../components/UI'

interface UnifiedGridRowProps {
  item: AsnAdjustDetail | AsnAdjustWorkOrderLpn
  index: number
  workOrderId: number
  hasLpns: boolean
  showName: boolean
  isExpanded: boolean
  onExpand: () => void
  onLineSaved: () => void
  externalOpenSnackBar?: (msg: string, status: boolean) => void
}

const isAsnAdjustDetail = (item: any): item is AsnAdjustDetail => {
  return 'skuId' in item && 'sku' in item
}

export const UnifiedGridRow: React.FC<UnifiedGridRowProps> = ({
  item,
  index,
  workOrderId,
  // hasLpns,
  showName,
  isExpanded,
  onExpand,
  onLineSaved,
  externalOpenSnackBar
}) => {
  const [isLoadingDetails, setIsLoadingDetails] = useState<boolean>(false)
  const hasLoadedRef = useRef(false)

  // Determinar el tipo de item
  const isDetailItem = isAsnAdjustDetail(item)
  const adjustItem = isDetailItem ? (item as AsnAdjustDetail) : null
  const lpnItem = !isDetailItem ? (item as AsnAdjustWorkOrderLpn) : null

  // Obtener skuId y lpn según el tipo de item
  const skuId = adjustItem?.skuId || lpnItem?.skuId || 0
  const lpn = adjustItem?.lpn || lpnItem?.lpn || '-'

  // Crear un ID único para esta fila para manejar el estado correctamente
  const rowUniqueId = useMemo(() => {
    if (adjustItem) {
      return `detail-${adjustItem.skuId}-${adjustItem.lpn || 'nolpn'}`
    }
    if (lpnItem) {
      return `lpn-${lpnItem.lpn}`
    }
    return `row-${index}`
  }, [adjustItem, lpnItem, index])

  const {
    loading,
    saveButtonState,
    items: adjustmentItems,
    totalItems,
    pagination,
    rowQuantities,
    rowComments,
    fetchInitialData,
    handlePaginationChange,
    handleQuantityChange,
    handleCommentChange,
    saveLine,
    saveAll
  } = useAdjustments(
    workOrderId,
    skuId,
    lpn !== '-' ? lpn : '',
    onLineSaved,
    externalOpenSnackBar
  )

  const handleSaveLine = useCallback(
    async (detailRowIndex: number, asnDetailId: number) => {
      try {
        // Buscar el índice real en adjustmentItems
        if (
          adjustmentItems.length > 0 &&
          detailRowIndex < adjustmentItems.length
        ) {
          const itemToSave = adjustmentItems[detailRowIndex]

          // Guardar la línea
          await saveLine(
            detailRowIndex,
            asnDetailId || itemToSave.asnDetailId || 0
          )
        } else {
          console.error(
            'Índice fuera de rango:',
            detailRowIndex,
            'total items:',
            adjustmentItems.length
          )
        }
      } catch (error) {
        console.error('Error en handleSaveLine:', error)
      }
    },
    [saveLine, adjustmentItems]
  )

  // Cargar detalles cuando se expande la fila
  useEffect(() => {
    if (!isExpanded || hasLoadedRef.current || !workOrderId) return

    const loadDetails = async () => {
      setIsLoadingDetails(true)
      hasLoadedRef.current = true

      try {
        // Preparar filtros según el tipo de item
        const filtersToApply: Filter[] = [
          {
            key: 'workOrderId',
            value: workOrderId,
            label: 'Work Order',
            displayValue: workOrderId.toString()
          }
        ]

        // Agregar skuId si está disponible
        if (skuId > 0) {
          filtersToApply.push({
            key: 'skuId',
            value: skuId,
            label: 'Sku',
            displayValue: skuId.toString()
          })
        }

        // Agregar LPN si está disponible y no es '-'
        if (lpn && lpn !== '-') {
          filtersToApply.push({
            key: 'LPN',
            value: lpn,
            label: 'lpn',
            displayValue: lpn
          })
        }

        // fetchInitialData para cargar datos
        await fetchInitialData(filtersToApply)
      } catch (error) {
        console.error('Error cargando detalles:', error)
        hasLoadedRef.current = false
      } finally {
        setIsLoadingDetails(false)
      }
    }

    loadDetails()
  }, [isExpanded, workOrderId, skuId, lpn, fetchInitialData, rowUniqueId])

  // Resetear el flag cuando se colapsa
  useEffect(() => {
    if (!isExpanded) {
      hasLoadedRef.current = false
    }
  }, [isExpanded])

  const handleFinalizeAdjustment = async () => {
    try {
      await saveAll()
    } catch (error) {
      console.error('Error finalizando ajuste:', error)
    }
  }

  // Obtener datos para mostrar en la fila (sin detalles expandidos)
  const getDisplayData = useMemo(() => {
    if (adjustItem) {
      return adjustItem
    }

    if (lpnItem) {
      return lpnItem
    }

    return {
      lpn: '-',
      skuImageUrl: '',
      sku: 'N/A',
      name: 'N/A',
      isPreSaleReception: false,
      unitsQty: 0,
      locatedQty: 0,
      receptionQty: 0
    } as Partial<AsnAdjustDetail & AsnAdjustWorkOrderLpn>
  }, [adjustItem, lpnItem])

  const displayData = getDisplayData

  const showSkuOrName = () => {
    return showName ? displayData.sku || '' : displayData.name || ''
  }

  const renderCellContent = (col: any) => {
    switch (col.dataField) {
      case 'expand':
        return (
          <IconButton
            size='small'
            onClick={onExpand}
            disabled={isLoadingDetails}
            sx={{ padding: '2px' }}
          >
            {isExpanded ? <KeyboardArrowUp /> : <KeyboardArrowDown />}
          </IconButton>
        )

      case 'lpn':
        return <Typography style={{ fontSize: '13px' }}>{displayData.lpn || '-'}</Typography>

      case 'producto':
        return displayData.skuImageUrl ? (
          <img
            src={displayData.skuImageUrl}
            style={{
              width: '50px',
              height: '50px',
              borderRadius: '4px',
              objectFit: 'cover'
            }}
            alt='Producto'
            onError={(e) => {
              ;(e.target as HTMLImageElement).style.display = 'none'
              const parent = e.currentTarget.parentElement
              if (parent) {
                const text = document.createElement('span')
                text.textContent = 'Sin Imagen'
                text.style.fontSize = '12px'
                text.style.color = '#666'
                parent.appendChild(text)
              }
            }}
          />
        ) : (
          <Typography
            variant='body2'
            style={{ color: '#666', fontSize: '12px' }}
          >
            Sin Imagen
          </Typography>
        )

      case 'nombre':
        return <CellText text={showSkuOrName()} textAlign={true} />

      case 'isPreSaleReception':
        return displayData.isPreSaleReception !== undefined ? (
          <StatusIcon value={displayData.isPreSaleReception} />
        ) : null

      case 'RealQty':
        return <CellText text={`${displayData.unitsQty || 0}`} />

      case 'LocatedQty':
        return <CellText text={`${displayData.locatedQty || 0}`} />

      case 'DiffereceQty':
        return <CellText text={`${displayData.receptionQty || 0}`} />

      case 'Actions':
        return (
          <Box
            display='flex'
            alignItems='center'
            justifyContent='center'
            style={{ gap: '8px' }}
          >
            {lpnItem && (
              <IconButton
                size='small'
                onClick={(event) => {
                  event.stopPropagation()
                  // Forzar recarga usando fetchInitialData
                  hasLoadedRef.current = false
                  const filtersToApply: Filter[] = [
                    {
                      key: 'workOrderId',
                      value: workOrderId,
                      label: 'Work Order',
                      displayValue: workOrderId.toString()
                    },
                    ...(skuId > 0
                      ? [
                          {
                            key: 'skuId',
                            value: skuId,
                            label: 'Sku',
                            displayValue: skuId.toString()
                          }
                        ]
                      : []),
                    ...(lpn && lpn !== '-'
                      ? [
                          {
                            key: 'LPN',
                            value: lpn,
                            label: 'lpn',
                            displayValue: lpn
                          }
                        ]
                      : [])
                  ]
                  fetchInitialData(filtersToApply)
                }}
                disabled={loading || isLoadingDetails}
              >
                <Sync />
              </IconButton>
            )}
            <Button
              color='primary'
              variant='contained'
              size='small'
              onClick={(event) => {
                event.stopPropagation()
                handleFinalizeAdjustment()
              }}
              disabled={saveButtonState || loading || isLoadingDetails}
              startIcon={<PublishedWithChanges />}
              sx={{
                fontSize: '0.875rem',
                padding: '6px 12px',
                borderRadius: '12px',
                textTransform: 'none',
                fontWeight: 500,
                transition: 'all 0.2s ease'
              }}
            >
              Finalizar Ajuste
            </Button>
          </Box>
        )

      default:
        return null
    }
  }

  // Obtener skuAdjust para pasar al detalle
  const getSkuAdjustForDetail = useMemo(() => {
    if (adjustItem) {
      return adjustItem
    }

    // Si es LPN pero tenemos adjustmentItems, usar datos combinados
    if (lpnItem && adjustmentItems.length > 0) {
      const firstItem = adjustmentItems[0]
      return {
        skuId: firstItem.skuId || 0,
        sku: firstItem.sku || '',
        name: firstItem.name || '',
        unitsQty: displayData.unitsQty || 0,
        locatedQty: displayData.locatedQty || 0,
        receptionQty: displayData.receptionQty || 0,
        excessQty: 0,
        skuImageUrl: displayData.skuImageUrl || '',
        lpn: lpnItem.lpn,
        ean13: '',
        isPreSaleReception: displayData.isPreSaleReception || false
      } as AsnAdjustDetail
    }

    // Datos por defecto
    return {
      skuId: skuId,
      sku: displayData.sku || '',
      name: displayData.name || '',
      unitsQty: displayData.unitsQty || 0,
      locatedQty: displayData.locatedQty || 0,
      receptionQty: displayData.receptionQty || 0,
      excessQty: 0,
      skuImageUrl: displayData.skuImageUrl || '',
      lpn: displayData.lpn || '-',
      ean13: '',
      isPreSaleReception: displayData.isPreSaleReception || false
    } as AsnAdjustDetail
  }, [adjustItem, lpnItem, adjustmentItems, skuId, displayData])

  return (
    <Fragment>
      <Paper
        elevation={0}
        style={{
          marginBottom: '2px',
          padding: '10px 12px',
          backgroundColor: '#ffffff',
          cursor: 'pointer',
          opacity: isLoadingDetails ? 0.7 : 1,
          border: '1px solid #e8e8e8',
          borderRadius: '6px',
          transition: 'all 0.2s ease'
        }}
        onClick={onExpand}
      >
        <Grid container alignItems='center' spacing={1}>
          {dataGridSettings.columns.map((col) => (
            <Grid
              item
              key={`${index}-${col.dataField}`}
              xs={col.xs}
              sm={col.sm}
              lg={col.lg}
              style={{ textAlign: 'center' }}
            >
              {renderCellContent(col)}
            </Grid>
          ))}
        </Grid>
      </Paper>

      <Collapse in={isExpanded}>
        <Box style={{ padding: '12px 16px', backgroundColor: '#f9f9f9', marginBottom: '2px', borderRadius: '6px', marginTop: '-4px' }}>
          {loading ? (
            <Box style={{ padding: '32px', textAlign: 'center' }}>
              <Loading />
              <Typography style={{ fontSize: '13px', color: '#666', marginTop: '8px' }}>Cargando detalles...</Typography>
            </Box>
          ) : adjustmentItems.length > 0 ? (
            <Fragment>
              <GridElementModalDetailNew
                paginationState={pagination}
                totalItems={totalItems}
                loading={loading}
                items={adjustmentItems}
                rowQuantities={rowQuantities}
                rowComments={rowComments}
                skuAdjust={getSkuAdjustForDetail}
                onQuantityChange={handleQuantityChange}
                onCommentChange={handleCommentChange}
                onSaveLine={handleSaveLine}
                onPaginationChange={handlePaginationChange}
              />
            </Fragment>
          ) : !loading ? (
            <Box style={{ padding: '32px', textAlign: 'center' }}>
              <Typography style={{ fontSize: '13px', color: '#999' }}>
                No hay detalles disponibles
              </Typography>
            </Box>
          ) : null}
        </Box>
      </Collapse>
    </Fragment>
  )
}
